// frontend/src/pages/NetworkPage.jsx
import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import networkService from '../services/networkService';
import SecureGate from '../components/Network/SecureGate';
import ConnectionCard from '../components/Network/ConnectionCard';
import { motion, AnimatePresence } from 'framer-motion';
import './NetworkPage.css';
import ProfileBackground from '../components/ProfileBackground';
import { useAppBackground } from '../context/AppBackgroundContext';
import { useNotification } from '../context/NotificationContext';
import ReadableText from '../components/ReadableText';
import ImageLightbox from '../components/ImageLightbox';
import { useSpeech } from '../context/SpeechContext';
import { useAudio } from '../context/AudioContext';
import { VOICE_INTENTS, normalizeVoiceText, sanitizeVoicePayload } from '../services/voiceCommandService';
import { useAgentCommands } from '../contexts/AgentCommandContext';
import { useI18n } from '../i18n';

const SPIDER_HINT_STORAGE_KEY = 'q_network_spider_hint_last_seen';
const SPIDER_HINT_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000;
const SPIDER_HINT_AUTO_HIDE_MS = 6500;

const NetworkPage = () => {
  const { addNotification, addConfirm } = useNotification();
  const { t, language } = useI18n();
  const { user, getSessionTime } = useContext(AuthContext);
  const Maps = useNavigate();
  const { displayedImage, settings } = useAppBackground() || {};
  const { lastCommand } = useSpeech() || {};
  const audio = useAudio();
  const { registerCommand, unregisterCommand } = useAgentCommands();

  const renderAvatar = (av) => {
    const cleanAv = av === '/default-avatar.png' ? null : av;
    if (!cleanAv) return '👤';
    if (cleanAv.startsWith('http')) {
      return <img src={cleanAv} alt="avatar" className="conn-avatar-img" referrerPolicy="no-referrer" />;
    }
    return <span className="conn-avatar-emoji">{cleanAv}</span>;
  };
  const [activeTab, setActiveTab] = useState('connections');
  const [unlocked, setUnlocked] = useState(false);
  const [hasAutoConnected, setHasAutoConnected] = useState(localStorage.getItem('hasAutoConnected') === 'true');
  const [lightboxImage, setLightboxImage] = useState(null);
  const [showAutoConnectModal, setShowAutoConnectModal] = useState(false);
  const [pinUsed, setPinUsed] = useState(null);
  const [connections, setConnections] = useState([]);
  const [groups, setGroups] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [newGroupTitle, setNewGroupTitle] = useState('');
  const [newGroupEndDate, setNewGroupEndDate] = useState('');
  const [invitedUsers, setInvitedUsers] = useState([]);
  const [inviteSearchQuery, setInviteSearchQuery] = useState('');
  const [inviteSearchResults, setInviteSearchResults] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showGroupInfo, setShowGroupInfo] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatMessagesEndRef = useRef(null);
  const processedVoiceCommandRef = useRef(null);

  const [isBottomBarOpen, setIsBottomBarOpen] = useState(false);
  const [expandedManifestos, setExpandedManifestos] = useState({});
  const [showSpiderHint, setShowSpiderHint] = useState(false);

  const textareaRef = useRef(null);

  const handleInputResize = (e) => {
    setNewMessage(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = `${e.target.scrollHeight}px`;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault(); // Evita el salto de línea
      handleSendMessage(e);
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'; // Reinicia altura al enviar
      }
    }
  };

  useEffect(() => {
    if (selectedGroup) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [selectedGroup]);

  useEffect(() => {
    setIsBottomBarOpen(false);
    if (activeTab !== 'groups') {
      setSelectedGroup(null); // <-- Cierra el chat activo al salir de grupos
    }
  }, [activeTab]);

  const markSpiderHintSeen = useCallback(() => {
    setShowSpiderHint(false);
    try {
      localStorage.setItem(SPIDER_HINT_STORAGE_KEY, String(Date.now()));
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (!unlocked || showCreateGroup) return undefined;

    let shouldShow = false;
    try {
      const lastSeen = Number(localStorage.getItem(SPIDER_HINT_STORAGE_KEY) || 0);
      shouldShow = !lastSeen || Date.now() - lastSeen >= SPIDER_HINT_INTERVAL_MS;
      if (shouldShow) {
        localStorage.setItem(SPIDER_HINT_STORAGE_KEY, String(Date.now()));
      }
    } catch (_) {
      shouldShow = true;
    }

    if (!shouldShow) return undefined;

    setShowSpiderHint(true);
    const timer = setTimeout(() => setShowSpiderHint(false), SPIDER_HINT_AUTO_HIDE_MS);
    return () => clearTimeout(timer);
  }, [unlocked, showCreateGroup]);

  const toggleSpiderMenu = () => {
    markSpiderHintSeen();
    if (selectedGroup) {
      setSelectedGroup(null);
      setIsBottomBarOpen(true);
    } else {
      setIsBottomBarOpen(prev => !prev);
    }
  };

  const openCreateGroupModal = () => {
    markSpiderHintSeen();
    setIsBottomBarOpen(false);
    setShowCreateGroup(true);
  };

  useEffect(() => {
    if (chatMessages.length > 0) {
        const lastMessage = chatMessages[chatMessages.length - 1];
        if (lastMessage.role === 'user') {
            chatMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        } else {
            const aiMessages = document.querySelectorAll('.chat-bubble.qoph');
            if (aiMessages.length > 0) {
                const lastAiMessage = aiMessages[aiMessages.length - 1];
                lastAiMessage.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
    }
  }, [chatMessages, chatLoading]);

  const handleGroupImageLoad = () => {
    const aiMessages = document.querySelectorAll('.chat-bubble.qoph');
    if (aiMessages.length > 0) {
        const lastAiMessage = aiMessages[aiMessages.length - 1];
        lastAiMessage.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const fetchChatHistory = useCallback(async (groupId) => {
    setChatLoading(true);
    try {
        const { data } = await networkService.getGroupChat(groupId);
        setChatMessages(Array.isArray(data) ? data : (data?.messages || data?.data || []));
    } catch (err) {
        console.error('Error fetching chat history:', err);
    } finally {
        setChatLoading(false);
    }
  }, []);

  const findGroupByVoiceName = useCallback((targetName = '') => {
    const normalizedTarget = normalizeVoiceText(targetName);
    if (!normalizedTarget) return null;
    return groups.find(group => {
      const title = normalizeVoiceText(group.title);
      return title === normalizedTarget || title.includes(normalizedTarget) || normalizedTarget.includes(title);
    }) || null;
  }, [groups]);

  const selectGroupForChat = useCallback((group) => {
    if (!group) return false;
    if (!(group.userStatus === 'active' || group.isArchived)) {
      addNotification(t('network.groupInactive', { title: group.title }), 'info');
      return false;
    }
    setActiveTab('groups');
    setSelectedGroup(group);
    setIsBottomBarOpen(false);
    fetchChatHistory(group.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    return true;
  }, [addNotification, fetchChatHistory, t]);

  const cleanReadableText = useCallback((value = '') => String(value)
    .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
    .replace(/https?:\/\/\S+\.(?:png|jpe?g|gif|webp)(?:\?\S*)?/gi, '')
    .replace(/\s+/g, ' ')
    .trim(), []);

  // Cargar conexiones cuando se desbloquea
  const fetchConnections = async (pin) => {
    setLoading(true);
    try {
      const data = await networkService.getConnections(pin || pinUsed);
      // Support both array and paginated object structure
      setConnections(data.connections || data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUnlock = (pin, initialData) => {
    setUnlocked(true);
    setPinUsed(pin);
    // Support both array and paginated object structure from initial load
    setConnections(initialData.connections || initialData);
    fetchGroups();
  };

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await networkService.getGroups();
      setGroups(Array.isArray(data) ? data : (data?.groups || []));
    } catch (err) {
      console.error('Error fetching groups:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleCreateGroup = async () => {
    if (!newGroupTitle || !newGroupEndDate) {
      addNotification(t('network.titleDateRequired'), 'info');
      return;
    }
    setLoading(true);
    try {
      await networkService.createGroup({
        title: newGroupTitle,
        endDate: newGroupEndDate,
        invitedHandles: invitedUsers.map(u => u.handle)
      });
      setShowCreateGroup(false);
      setNewGroupTitle('');
      setNewGroupEndDate('');
      setInvitedUsers([]);
      fetchGroups();
    } catch (err) {
      addNotification(err.response?.data?.error || t('network.createGroupError'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateMembership = async (groupId, status) => {
    try {
      await networkService.updateGroupMembership(groupId, status);
      fetchGroups();
    } catch (err) {
      addNotification(t('network.membershipError'), 'error');
    }
  };

  const handleSendMessage = async (e, dictatedMessage) => {
    e?.preventDefault?.();
    const candidateMessage = dictatedMessage ?? newMessage;
    if (!candidateMessage.trim() || chatLoading) return;

    const msg = sanitizeVoicePayload(candidateMessage);
    setNewMessage('');

    // Optimistic update
    const tempMsg = { id: Date.now(), role: 'user', content: msg, createdAt: new Date().toISOString() };
    setChatMessages(prev => [...prev, tempMsg]);

    setChatLoading(true);
    try {
        const { data } = await networkService.sendGroupMessage(selectedGroup.id, msg, language);
        setChatMessages(prev => [...prev.filter(m => m.id !== tempMsg.id), tempMsg, data]);
    } catch (err) {
        addNotification(t('network.sendError'), 'error');
        setChatMessages(prev => prev.filter(m => m.id !== tempMsg.id));
    } finally {
        setChatLoading(false);
    }
  };


  const sendVoiceMessageToGroup = useCallback(async (group, message) => {
    const msg = sanitizeVoicePayload(message);
    if (!group || !msg || chatLoading) return false;
    if (!(group.userStatus === 'active' || group.isArchived)) {
      addNotification(t('network.groupCannotReceive', { title: group.title }), 'info');
      return false;
    }

    const isCurrentGroup = selectedGroup?.id === group.id;
    setActiveTab('groups');
    setSelectedGroup(group);
    setIsBottomBarOpen(false);
    setNewMessage(msg);

    const tempMsg = { id: Date.now(), role: 'user', content: msg, createdAt: new Date().toISOString() };
    if (isCurrentGroup) setChatMessages(prev => [...prev, tempMsg]);

    setChatLoading(true);
    try {
      const { data } = await networkService.sendGroupMessage(group.id, msg, language);
      if (isCurrentGroup) {
        setChatMessages(prev => [...prev.filter(m => m.id !== tempMsg.id), tempMsg, data]);
      } else {
        const history = await networkService.getGroupChat(group.id);
        const historyMessages = Array.isArray(history.data) ? history.data : (history.data?.messages || history.data?.data || []);
        setChatMessages(historyMessages);
      }
      setNewMessage('');
      return true;
    } catch (err) {
      addNotification(t('network.dictatedSendError'), 'error');
      if (isCurrentGroup) setChatMessages(prev => prev.filter(m => m.id !== tempMsg.id));
      return false;
    } finally {
      setChatLoading(false);
    }
  }, [addNotification, chatLoading, selectedGroup, language, t]);

  useEffect(() => {
    registerCommand('network_toggle_bottom_bar', () => {
      setIsBottomBarOpen((prev) => !prev);
      return true;
    });

    registerCommand('network_open_groups', () => {
      if (!unlocked) {
        addNotification(t('network.unlockGroups'), 'info');
        return false;
      }
      setActiveTab('groups');
      setSelectedGroup(null);
      setIsBottomBarOpen(true);
      fetchGroups();
      return { ok: true };
    });

    registerCommand('network_close_groups', () => {
      setSelectedGroup(null);
      setIsBottomBarOpen(false);
      return { ok: true };
    });

    registerCommand('network_read_groups', () => {
      if (!unlocked) {
        addNotification(t('network.unlockGroups'), 'info');
        return false;
      }
      setActiveTab('groups');
      const names = groups.map(group => group.title).filter(Boolean);
      return { ok: true, text: names.length ? names.join(', ') : '' };
    });

    registerCommand('network_read_manifesto', () => {
      const visibleGroup = selectedGroup || groups[0];
      const manifesto = cleanReadableText(visibleGroup?.manifesto);
      if (!manifesto) return false;
      audio?.speak(manifesto, { label: `${t('network.manifesto')} ${visibleGroup.title}` });
      return true;
    });

    registerCommand('network_send_message', async (text) => {
      const message = sanitizeVoicePayload(text);
      if (!selectedGroup || !message) {
        addNotification(t('network.groupMissing', { name: t('network.groups') }), 'info');
        return { ok: false };
      }
      const sent = await sendVoiceMessageToGroup(selectedGroup, message);
      return { ok: Boolean(sent) };
    });

    registerCommand('network_select_group', (target) => {
      const group = findGroupByVoiceName(target);
      if (!group) return false;
      return selectGroupForChat(group);
    });

    return () => {
      unregisterCommand('network_toggle_bottom_bar');
      unregisterCommand('network_open_groups');
      unregisterCommand('network_close_groups');
      unregisterCommand('network_read_groups');
      unregisterCommand('network_read_manifesto');
      unregisterCommand('network_send_message');
      unregisterCommand('network_select_group');
    };
  }, [addNotification, audio, cleanReadableText, fetchGroups, findGroupByVoiceName, groups, registerCommand, selectGroupForChat, selectedGroup, sendVoiceMessageToGroup, t, unlocked, unregisterCommand]);

  useEffect(() => {
    const analysis = lastCommand?.analysis;
    if (!analysis?.handled || processedVoiceCommandRef.current === lastCommand.timestamp) return;

    const isGroupsUiAction = analysis.intent === VOICE_INTENTS.UI_ACTION && ['TOGGLE_GROUPS_BAR', 'CLOSE_CHAT', 'SHOW_MANIFESTO'].includes(analysis.action);
    const isGroupSelect = analysis.intent === VOICE_INTENTS.SELECT_TARGET && analysis.targetType === 'group';
    const isManifestoRead = analysis.intent === VOICE_INTENTS.READ_OUT_LOUD && analysis.target === 'manifesto';
    const isGroupSend = analysis.intent === VOICE_INTENTS.SEND_VOICE_MESSAGE && analysis.targetType === 'group';

    if (!isGroupsUiAction && !isGroupSelect && !isManifestoRead && !isGroupSend) return;
    processedVoiceCommandRef.current = lastCommand.timestamp;

    if (isGroupsUiAction) {
      if (analysis.action === 'TOGGLE_GROUPS_BAR') {
        if (!unlocked) {
          addNotification(t('network.unlockGroups'), 'info');
          return;
        }
        setActiveTab('groups');
        setSelectedGroup(null);
        setIsBottomBarOpen(true);
        fetchGroups();
        return;
      }
      if (analysis.action === 'CLOSE_CHAT') {
        setSelectedGroup(null);
        setIsBottomBarOpen(false);
        return;
      }
      if (analysis.action === 'SHOW_MANIFESTO') {
        const visibleGroup = selectedGroup || groups[0];
        if (visibleGroup?.id) {
          setExpandedManifestos(prev => ({ ...prev, [visibleGroup.id]: true }));
          setShowGroupInfo(!!selectedGroup);
        }
        return;
      }
    }

    if (isGroupSelect) {
      const group = findGroupByVoiceName(analysis.targetName);
      if (!group) {
        addNotification(t('network.groupMissing', { name: analysis.targetName }), 'info');
        return;
      }
      selectGroupForChat(group);
      return;
    }

    if (isManifestoRead) {
      const visibleGroup = selectedGroup || groups.find(group => expandedManifestos[group.id]) || groups[0];
      const manifesto = cleanReadableText(visibleGroup?.manifesto);
      if (!manifesto) {
        addNotification(t('network.noManifesto'), 'info');
        return;
      }
      audio?.speak(manifesto, { label: `${t('network.manifesto')} ${visibleGroup.title}` });
      return;
    }

    if (isGroupSend) {
      const group = findGroupByVoiceName(analysis.targetName) || selectedGroup;
      if (!group) {
        addNotification(t('network.groupMissing', { name: analysis.targetName }), 'info');
        return;
      }
      sendVoiceMessageToGroup(group, analysis.message);
    }
  }, [addNotification, audio, cleanReadableText, expandedManifestos, fetchGroups, findGroupByVoiceName, groups, lastCommand, selectGroupForChat, selectedGroup, sendVoiceMessageToGroup, unlocked, t]);

  const toggleInvitedUser = (u) => {
    // Verificar si es mutual follow antes de permitir invitar
    const isMutual = connections.find(c => c.handle === u.handle)?.is_mutual;
    if (!isMutual) {
      addNotification(t('network.mutualRequired', { handle: u.handle }), 'info');
      return;
    }

    if (invitedUsers.find(iu => iu.handle === u.handle)) {
      setInvitedUsers(prev => prev.filter(iu => iu.handle !== u.handle));
    } else {
      if (invitedUsers.length >= 15) {
        addNotification(t('network.inviteLimit'), 'info');
        return;
      }
      // Asegurarse de tener el ID para el backend
      const fullUser = connections.find(c => c.handle === u.handle) || u;
      setInvitedUsers(prev => [...prev, fullUser]);
    }
  };

  // Búsqueda para invitaciones (fusionada con conexiones)
  useEffect(() => {
    if (!showCreateGroup || !inviteSearchQuery) {
        setInviteSearchResults([]);
        return;
    }

    const timer = setTimeout(async () => {
        setLoading(true);
        try {
            const { data } = await networkService.search(inviteSearchQuery);
            setInviteSearchResults(data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, 500);

    return () => clearTimeout(timer);
  }, [inviteSearchQuery, showCreateGroup]);

  const handleUnfollow = async (handle) => {
    addConfirm(t('network.disconnectConfirm', { handle }), async () => {
      try {
        await networkService.unfollow(handle);
        setConnections(prev => prev.filter(c => c.handle !== handle));
      } catch (err) {
        addNotification(t('network.disconnectError'), 'error');
      }
    });
  };

  const handleFollow = async (handle) => {
    // Intercepción para Easter Egg: Auto-conexión
    if (handle === user?.qHandle) {
      const time = getSessionTime();
      if (time < 120) {
        addNotification(t('network.syncRejected'), 'info');
        return;
      } else {
        setShowAutoConnectModal(true);
        return;
      }
    }

    try {
      await networkService.follow(handle);
      addNotification(t('network.nowFollowing', { handle }), 'success');
      // Actualizar UI de búsqueda
      setSearchResults(prev => prev.map(u => u.handle === handle ? { ...u, following: true } : u));
      if (unlocked) fetchConnections();
    } catch (err) {
      addNotification(t('network.followError'), 'error');
    }
  };

  const renderGroupsNetworkMap = () => {
    const hasRealGroups = Array.isArray(groups) && groups.length > 0;
    const visibleGroups = hasRealGroups ? groups.slice(0, 6) : [];
    const nodes = [];
    const links = [];
    const centerNode = {
      id: 'network-self',
      kind: 'self',
      label: user?.qHandle ? `@${user.qHandle}` : t('network.groupsMapYou'),
      x: 50,
      y: 52,
    };

    nodes.push(centerNode);

    if (hasRealGroups) {
      const groupCount = Math.max(visibleGroups.length, 1);
      visibleGroups.forEach((group, index) => {
        const angle = (-90 + (360 / groupCount) * index) * (Math.PI / 180);
        const groupNode = {
          id: `group-${group.id}`,
          kind: 'group',
          label: group.title,
          group,
          x: 50 + Math.cos(angle) * 31,
          y: 52 + Math.sin(angle) * 25,
        };

        nodes.push(groupNode);
        links.push({ id: `self-${group.id}`, from: centerNode, to: groupNode });

        (group.members || []).slice(0, 2).forEach((member, memberIndex) => {
          const memberAngle = angle + (memberIndex === 0 ? -0.45 : 0.45);
          const memberNode = {
            id: `member-${group.id}-${member.handle || memberIndex}`,
            kind: 'member',
            label: member.displayName || member.name || member.handle || t('network.groupsMapGuest'),
            member,
            x: groupNode.x + Math.cos(memberAngle) * 14,
            y: groupNode.y + Math.sin(memberAngle) * 12,
          };

          nodes.push(memberNode);
          links.push({ id: `group-${group.id}-${member.handle || memberIndex}`, from: groupNode, to: memberNode });
        });
      });
    } else {
      const previewNodes = [
        { id: 'preview-group', kind: 'group preview', label: t('network.groupsMapMentalGroup'), x: 50, y: 22 },
        { id: 'preview-qoph', kind: 'qoph preview', label: t('network.groupsMapQoph'), x: 78, y: 52 },
        { id: 'preview-guest', kind: 'member preview', label: t('network.groupsMapGuest'), x: 50, y: 82 },
        { id: 'preview-idea', kind: 'idea preview', label: t('network.groupsMapSharedIdea'), x: 22, y: 52 },
      ];

      previewNodes.forEach(node => {
        nodes.push(node);
        links.push({ id: `preview-link-${node.id}`, from: centerNode, to: node });
      });
    }

    return (
      <section className={`groups-network-map ${hasRealGroups ? 'real' : 'preview'}`}>
        <div className="groups-map-copy">
          {!hasRealGroups && <span className="groups-map-badge">{t('network.groupsMapPreviewBadge')}</span>}
          <h2>{hasRealGroups ? t('network.groupsMapTitle') : t('network.groupsMapEmptyTitle')}</h2>
          {!hasRealGroups && <p>{t('network.groupsMapEmptyBody')}</p>}
        </div>

        <div className="groups-map-canvas" role="list" aria-label={t('network.groupsMapTitle')}>
          <svg className="groups-map-lines" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
            {links.map(link => (
              <line
                key={link.id}
                x1={link.from.x}
                y1={link.from.y}
                x2={link.to.x}
                y2={link.to.y}
              />
            ))}
          </svg>

          {nodes.map(node => {
            const className = `groups-network-node ${node.kind} ${node.group?.isArchived ? 'archived' : ''} ${node.group?.userStatus || ''}`;
            const style = { left: `${node.x}%`, top: `${node.y}%` };
            const canOpenGroup = node.group && (node.group.userStatus === 'active' || node.group.isArchived);
            const nodeContent = (
              <>
                <span className="groups-node-orb">
                  {node.member ? renderAvatar(node.member.avatar || node.member.avatar_url) : node.kind.includes('qoph') ? 'Q' : node.kind.includes('idea') ? '✦' : node.kind.includes('group') ? '◎' : t('network.groupsMapYou')}
                </span>
                <span className="groups-node-label">{node.label}</span>
                {node.group?.members?.length > 2 && (
                  <span className="groups-node-meta">+{node.group.members.length - 2}</span>
                )}
              </>
            );

            return canOpenGroup ? (
              <button
                key={node.id}
                type="button"
                className={className}
                style={style}
                role="listitem"
                onClick={() => selectGroupForChat(node.group)}
                aria-label={`${t('network.groupsMapTitle')}: ${node.label}`}
              >
                {nodeContent}
              </button>
            ) : (
              <div key={node.id} className={className} style={style} role="listitem" aria-label={node.label}>
                {nodeContent}
              </div>
            );
          })}
        </div>
      </section>
    );
  };

  // Búsqueda con debounce manual
  useEffect(() => {
    if (activeTab !== 'search' || !searchQuery) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await networkService.search(searchQuery);
        // Marcar los que ya seguimos si estamos en la lista de conexiones
        const results = data.map(u => ({
          ...u,
          following: connections.some(c => c.handle === u.handle)
        }));
        setSearchResults(results);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery, activeTab, connections]);

  return (
    <>
      {settings?.enabled && displayedImage && <ProfileBackground imageData={displayedImage} interactive={false} />}
      <div className="network-page" style={{ position: 'relative', zIndex: 2 }}>
      <div className="network-header">
        {hasAutoConnected && (
          <button
            className="icon-btn-left"
            onClick={() => Maps('/auto-conexion')}
            title={t('network.autoConnection')}
          >
            🔑
          </button>
        )}

        <div className="network-identity">
          <h1>{t('network.title')}</h1>
          <div className="spider-hit-area network-spider-identity">
            {unlocked && !showCreateGroup && showSpiderHint && (
              <div className="spider-hint" role="status">
                <strong>{t('network.spiderHintTitle')}</strong>
                <span>{t('network.spiderHintBody')}</span>
                <button type="button" onClick={markSpiderHintSeen} aria-label={t('network.spiderHintDismiss')}>
                  ×
                </button>
              </div>
            )}
            {unlocked && !showCreateGroup ? (
              <button
                id="btn-network-spider"
                type="button"
                className={`bottom-bar-toggle-btn ${isBottomBarOpen ? 'open' : ''}`}
                onClick={toggleSpiderMenu}
                aria-expanded={isBottomBarOpen}
                aria-controls="network-groups-menu"
                title={t('network.spiderHintTitle')}
                aria-label={t('network.spiderHintTitle')}
              >
                🕸️
              </button>
            ) : (
              <div className="bottom-bar-toggle-btn identity-static" aria-hidden="true">
                🕸️
              </div>
            )}
          </div>
        </div>

        <button
          className="icon-btn-right"
          onClick={() => Maps('/ajustes#red-silenciosa')}
          title={t('network.networkSettings')}
        >
          ⚙️
        </button>
      </div>

      <div className="network-tabs three-tabs">
        <button
          className={`tab-btn ${activeTab === 'connections' ? 'active' : ''}`}
          onClick={() => setActiveTab('connections')}
        >
          {unlocked ? t('network.connections') : t('network.lockedConnections')}
        </button>
        <button
          className={`tab-btn ${activeTab === 'groups' ? 'active' : ''}`}
          onClick={() => {
            if (unlocked) {
                setActiveTab('groups');
                fetchGroups();
            } else {
                setActiveTab('connections');
                addNotification(t('network.unlockFirst'), 'info');
            }
          }}
        >
          {t('network.groups')}
        </button>
        <button
          className={`tab-btn ${activeTab === 'search' ? 'active' : ''}`}
          onClick={() => setActiveTab('search')}
        >
          {t('network.search')}
        </button>
      </div>

      <AnimatePresence mode="wait">
        {selectedGroup ? (
          <motion.div
            key="group-chat"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="group-chat-container"
          >
            <div className="chat-header">
               <button className="back-btn" onClick={() => setSelectedGroup(null)}>← {t('common.back')}</button>
               <div className="chat-title">
                  <h2>{selectedGroup.title}</h2>
                  <span className="qoph-label">{t('network.soul')}</span>
               </div>
               <button className="info-btn" onClick={() => setShowGroupInfo(true)}>ℹ️ {t('common.details')}</button>
            </div>

            <div className="chat-messages" id="qoph-chat-history">
                {(Array.isArray(chatMessages) ? chatMessages : []).map(m => (
                    <div key={m.id} className={`chat-bubble ${m.role}`} data-agent-role={m.role === 'qoph' ? 'qoph' : undefined}>
                        <div className="bubble-content">
                          {m.role === 'qoph' ? (
                            <ReadableText text={m.content} label={t('network.readQophMessage')}>
                              {m.content}
                            </ReadableText>
                          ) : (
                            m.content
                          )}
                        </div>
                        {m.imageUrl && (
                          <div className="bubble-image" onClick={() => setLightboxImage(m.imageUrl)} style={{ cursor: 'pointer' }}>
                            <img
                                src={m.imageUrl}
                                alt={t('network.groupImageAlt')}
                                className="qoph-revelation-img"
                                onLoad={handleGroupImageLoad}
                            />
                          </div>
                        )}
                        <div className="bubble-time">
                          {m.createdAt ? new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                        </div>
                    </div>
                ))}
                {chatLoading && (
                    <div className="chat-bubble qoph thinking">
                        <div className="bubble-content">{t('network.qophTuning')}</div>
                    </div>
                )}
                {chatMessages.length === 0 && !chatLoading && (
                    <div className="empty-chat">{t('network.emptyChat')}</div>
                )}
                <div ref={chatMessagesEndRef} />
            </div>

            <form className="chat-input-area" onSubmit={handleSendMessage}>
                <textarea
                    id="qoph-chat-input"
                    ref={textareaRef}
                    rows={1}
                    placeholder={t('network.inputPlaceholder')}
                    value={newMessage}
                    onChange={handleInputResize}
                    onKeyDown={handleKeyDown}
                    disabled={chatLoading}
                />
                <button id="btn-send-qoph" type="submit" disabled={chatLoading || !newMessage.trim()}>
                    {t('common.send')}
                </button>
            </form>
          </motion.div>
        ) : activeTab === 'connections' && (
          <motion.div
            key="connections-tab"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="connections-list"
          >
            {!unlocked ? (
              <SecureGate
                onUnlock={handleUnlock}
                hasPin={!!user?.has_pin || !!user?.qPinHash}
              />
            ) : (
              <>
                {loading && <div className="loading-spinner">{t('network.syncingConsciousness')}</div>}
                {!loading && connections.length === 0 && (
                  <div className="empty-state">{t('network.emptyConnections')}</div>
                )}
                {connections.map(conn => (
                  <ConnectionCard
                    key={conn.handle}
                    user={conn}
                    onUnfollow={handleUnfollow}
                  />
                ))}
              </>
            )}
          </motion.div>
        )}

        {activeTab === 'groups' && unlocked && (
          <motion.div
            key="groups-tab"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="groups-tab-content"
          >
            {!selectedGroup && (
              loading ? (
                <div className="loading-spinner">{t('network.syncingGroups')}</div>
              ) : (
                renderGroupsNetworkMap()
              )
            )}
          </motion.div>
        )}

        {activeTab === 'search' && (
          <motion.div
            key="search-tab"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="search-tab-content"
          >
            <div className="search-container">
              <input
                type="text"
                className="search-input"
                placeholder={t('network.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
            </div>

            <div className="search-results">
              {loading && <div className="loading-spinner">{t('network.searching')}</div>}
              {searchResults.map(u => (
                <div key={u.handle} className="search-item">
                  {renderAvatar(u.avatar || u.avatar_url)}
                  <div className="conn-info">
                    <span className="conn-name">{u.displayName || u.handle}</span>
                    <span className="conn-handle">@{u.handle}</span>
                    <span className="conn-bio"><ReadableText text={u.bio_snippet} label={t('network.readBio')}>{u.bio_snippet}</ReadableText></span>
                  </div>
                  <button
                    className="follow-btn"
                    onClick={() => handleFollow(u.handle)}
                    disabled={u.following}
                  >
                    {u.following ? t('common.following') : t('common.connect')}
                  </button>
                </div>
              ))}
              {!loading && searchQuery && searchResults.length === 0 && (
                <div className="empty-state">{t('network.noSearchResults')}</div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {unlocked && (
        <>
          {!showCreateGroup && (
            <>
              {isBottomBarOpen && !selectedGroup && (
                <div className="bottom-bar-backdrop" onClick={() => setIsBottomBarOpen(false)}></div>
              )}

              <div id="network-groups-menu" className={`groups-bottom-bar ${isBottomBarOpen ? 'open' : ''}`}>
                <div className="groups-bottom-bar-content">
                  <div className="groups-header">
                    <button className="create-group-btn" onClick={openCreateGroupModal}>
                      + {t('network.createGroup')}
                    </button>
                  </div>

              <div className="groups-list">
                {loading && <div className="loading-spinner">{t('network.syncingGroups')}</div>}
                {!loading && (!groups || groups.length === 0) && (
                  <div className="empty-state">{t('network.noGroups')}</div>
                )}
                {groups?.map(group => (
                  <div
                    key={group.id}
                    data-agent-group-name={group.title}
                    className={`group-card ${group.isArchived ? 'archived' : ''}`}
                    onClick={() => {
                      if (group.userStatus === 'active' || group.isArchived) {
                        setSelectedGroup(group);
                        setIsBottomBarOpen(false);
                        fetchChatHistory(group.id);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                    }}
                    style={{ cursor: (group.userStatus === 'active' || group.isArchived) ? 'pointer' : 'default' }}
                  >
                    <div className="group-card-header">
                      <div className="group-title-area">
                        <h3>{group.title}</h3>
                        <div className="group-members-cluster">
                          {group.members?.slice(0, 5).map((m, idx) => (
                            <div key={idx} className="mini-avatar" title={`@${m.handle}`}>
                              {renderAvatar(m.avatar)}
                            </div>
                          ))}
                          {group.members?.length > 5 && (
                            <div className="mini-avatar extra">+{group.members.length - 5}</div>
                          )}
                        </div>
                      </div>
                      <span className={`status-badge ${group.userStatus}`}>
                        {group.userStatus === 'pending' ? t('network.invitation') : group.userStatus === 'active' ? t('network.active') : t('network.abandoned')}
                      </span>
                    </div>

                    <div className="manifesto-box" id={selectedGroup?.id === group.id ? 'network-selected-manifesto' : undefined}>
                      <h4>{t('network.manifesto')}</h4>
                      <p className={`manifesto-text ${!expandedManifestos[group.id] ? 'collapsed' : ''}`}>
                        {group.manifesto ? (
                          <ReadableText text={group.manifesto} label={t('network.readManifesto')}>
                            {group.manifesto}
                          </ReadableText>
                        ) : (
                          t('network.generatingGroupSoul')
                        )}
                      </p>
                      {group.manifesto && (
                        <button
                          className="manifesto-toggle-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedManifestos(prev => ({ ...prev, [group.id]: !prev[group.id] }));
                          }}
                        >
                          {expandedManifestos[group.id] ? `▲ ${t('network.showLess')}` : `▼ ${t('network.showMore')}`}
                        </button>
                      )}

                      {group.firstQuery && (
                        <span className="first-query-hint">
                          {t('network.firstConnection', { query: `${group.firstQuery.substring(0, 60)}${group.firstQuery.length > 60 ? '...' : ''}` })}
                        </span>
                      )}

                      {group.isArchived && <span className="archive-note">{t('network.archived')}</span>}
                    </div>

                    {!group.isArchived && (
                      <div className="group-info">
                          <p>{t('network.ends', { date: new Date(group.endDate).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' }) })}</p>
                          <p>{t('network.members', { active: group.activeMemberCount, total: group.memberCount })}</p>
                      </div>
                    )}

                    {!group.isArchived && group.userStatus === 'pending' && (
                      <div className="group-actions" onClick={(e) => e.stopPropagation()}>
                        <button className="accept-btn" onClick={() => handleUpdateMembership(group.id, 'active')}>{t('common.accept')}</button>
                        <button className="leave-btn" onClick={() => handleUpdateMembership(group.id, 'left')}>{t('common.ignore')}</button>
                      </div>
                    )}
                    {!group.isArchived && group.userStatus === 'active' && (
                      <div className="group-actions" onClick={(e) => e.stopPropagation()}>
                        <button className="leave-btn" onClick={() => handleUpdateMembership(group.id, 'left')}>{t('common.leave')}</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              </div>
              </div>
            </>
          )}

          {showCreateGroup && (
            <div className="modal-overlay">
              <div className="create-group-modal">
                <h2>{t('network.createGroupTitle')}</h2>
                <input
                  type="text"
                  placeholder={t('network.groupTitlePlaceholder')}
                  value={newGroupTitle}
                  onChange={(e) => setNewGroupTitle(e.target.value)}
                  className="group-input"
                />
                <div className="input-label">{t('network.endDate')}</div>
                <input
                  type="datetime-local"
                  value={newGroupEndDate}
                  onChange={(e) => setNewGroupEndDate(e.target.value)}
                  className="group-input"
                />

                <div className="invite-section">
                  <h3>{t('network.inviteMembers')}</h3>

                  <div className="invite-search-box">
                    <input
                      type="text"
                      placeholder={t('network.invitePlaceholder')}
                      value={inviteSearchQuery}
                      onChange={(e) => setInviteSearchQuery(e.target.value)}
                      className="group-input"
                    />
                  </div>

                  <div className="connections-selector">
                    {inviteSearchQuery ? (
                      inviteSearchResults.map(u => {
                        const isMutual = connections.find(c => c.handle === u.handle)?.is_mutual;
                        return (
                          <div
                            key={u.handle}
                            className={`selector-item ${invitedUsers.find(iu => iu.handle === u.handle) ? 'selected' : ''} ${!isMutual ? 'not-mutual' : ''}`}
                            onClick={() => toggleInvitedUser(u)}
                          >
                            {renderAvatar(u.avatar_url || u.avatar)}
                            <span>@{u.handle}</span>
                            {!isMutual && <span className="warning-tag">{t('network.notMutual')}</span>}
                          </div>
                        );
                      })
                    ) : (
                      connections.filter(c => c.is_mutual).map(conn => (
                        <div
                          key={conn.handle}
                          className={`selector-item ${invitedUsers.find(iu => iu.handle === conn.handle) ? 'selected' : ''}`}
                          onClick={() => toggleInvitedUser(conn)}
                        >
                          {renderAvatar(conn.avatar_url)}
                          <span>@{conn.handle}</span>
                        </div>
                      ))
                    )}

                    {!inviteSearchQuery && connections.filter(c => c.is_mutual).length === 0 && (
                      <p className="hint">{t('network.noMutualHint')}</p>
                    )}
                  </div>

                  {invitedUsers.length > 0 && (
                    <div className="invited-chips">
                      {invitedUsers.map(u => (
                        <span key={u.handle} className="invited-chip" onClick={() => toggleInvitedUser(u)}>
                          @{u.handle} ✕
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="modal-actions">
                  <button className="cancel-btn" onClick={() => setShowCreateGroup(false)}>{t('common.cancel')}</button>
                  <button className="confirm-btn" onClick={handleCreateGroup} disabled={loading}>
                    {loading ? t('network.creating') : t('network.createGroupButton')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {showAutoConnectModal && (
        <div className="auto-connect-modal-overlay">
          <div className="auto-connect-modal">
            <h2>{t('network.autoWarningTitle')}</h2>
            <p>{t('network.autoWarningBody')}</p>
            <div className="modal-actions">
              <button className="abort-btn" onClick={() => setShowAutoConnectModal(false)}>{t('network.abort')}</button>
              <button className="start-btn" onClick={() => {
                localStorage.setItem('hasAutoConnected', 'true');
                setHasAutoConnected(true);
                Maps('/auto-conexion');
              }}>{t('network.startCalibration')}</button>
            </div>
          </div>
        </div>
      )}

      {showGroupInfo && selectedGroup && (
        <div className="modal-overlay" onClick={() => setShowGroupInfo(false)}>
          <div className="group-info-modal" onClick={(e) => e.stopPropagation()}>
            <h2>{t('network.groupDetailsTitle')}</h2>
            <div className="info-content">
              <h3>{selectedGroup.title}</h3>
              {selectedGroup.manifesto && (
                <div className="manifesto-box" id="network-selected-manifesto">
                  <h4>{t('network.soulManifesto')}</h4>
                  <p><ReadableText text={selectedGroup.manifesto} label={t('network.readSoulManifesto')}>{selectedGroup.manifesto}</ReadableText></p>
                </div>
              )}
              <div className="info-meta">
                <p><strong>{t('network.autoDestruction')}</strong> {new Date(selectedGroup.endDate).toLocaleString()}</p>
              </div>

              <h4>{t('network.consciousnessMembers')}</h4>
              <div className="member-list">
                {selectedGroup.members && selectedGroup.members.map((member, idx) => (
                  <div key={idx} className="member-list-item">
                    {renderAvatar(member.avatar)}
                    <div className="member-info">
                      <span className="member-handle">@{member.handle}</span>
                      <span className={`status-badge ${member.status}`}>
                        {member.status === 'active' ? t('network.active') : member.status === 'pending' ? t('network.pending') : t('network.left')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setShowGroupInfo(false)}>{t('common.close')}</button>
            </div>
          </div>
        </div>
      )}
      {lightboxImage && (
        <ImageLightbox
          src={lightboxImage}
          onClose={() => setLightboxImage(null)}
        />
      )}
      </div>
    </>
  );
};

export default NetworkPage;
